import {
	DialogBackdropState,
	Step,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { HubItemCategoryKind } from "client/Api/Hub/hub";
import {
	HubItemCategoryNodeKind,
	HubItemImageRestrictionsModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	ExistingHubItemImages,
	BlobIdImageHubMediaComposerViewModel,
	HubImageMediaKind,
	BlobIdImageElementComposerViewModel,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel
} from "client/Api/Hub/common.composer";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	isNotEmpty,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { configureValidationStep, IObjectValidationStepBuilder } from "client/Validation/steps";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { HubCategorySavedResult, useEditHubCategoryDialog } from "../../EditHubCategoryDialog/EditHubCategoryDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	AnnouncementSaveModel,
	AnnouncementViewModel,
	EditAnnouncementPageViewModel,
	getNewModel,
	getEditModel,
	create,
	PublishedAnnouncementSaveModel,
	update,
	updatePublished,
	AnnouncementStatus,
	CheckAnnouncementPinLimitResult,
	checkPinLimit,
	AnnouncementSaveRequestModel
} from "client/Api/Announcements/announcements";
import { ContentStep, ContentStepHandle } from "./ContentStep";
import { SettingsStep } from "./SettingsStep";
import { RecipientsStep } from "./RecipientsStep";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import {
	cleanUpMedia,
	convertLayoutToSaveModel,
	convertMediaToSaveModel,
	getOriginalImagesFromLayout
} from "client/Features/Hub/converters";
import { ArchivedContentStep } from "./ArchivedContentStep";
import { usePinnedAnnouncementsDialog } from "../PinnedAnnouncementsDialog/PinnedAnnouncementsDialog";
import { MAX_TAG_COUNT } from "client/Features/Hub/Builder/HubTagSelect";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	createLayoutValidation,
	createMediaValidation
} from "client/Features/Hub/Builder/validation";
import {
	convertToLinkedDocumentFormData,
	convertToLinkedDocumentSaveModel,
	MAX_LINKED_DOCUMENTS_COUNT
} from "client/Features/Hub/Management/LinkedDocuments";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import { IValueInputAttachable } from "client/Forms/attachable";
import { useHubComposerUnsavedChangesDialog } from "../../HubComposerUnsavedChangesDialog";
import { PathRedirect } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import {
	findHubCategoryItemNodes,
	getDisableTheShowAutomaticallySettingDialogProps,
	HubItemLayoutFormModel,
	HubItemMediaFormModel,
	tryFindHubCategoryItemNode
} from "../../common";
import { Preview } from "./Preview";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { never } from "client_legacy/ndaw/errors";
import { InfoAlertOptions, useInfoAlerts } from "../../HubItemComposerDialog/useInfoAlerts";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { getLinkedDocumentId } from "client/Features/Hub/useAddDocuments";
import { LinkedDocumentFormData } from "client/Features/Hub/common";

enum FormStep {
	Content = 1,
	Settings = 2,
	Recipients = 3
}

export type AnnouncementFormModel = OverrideProperties<
	AnnouncementSaveModel,
	{
		linkedDocuments: readonly LinkedDocumentFormData[];
		media: HubItemMediaFormModel;
		layout: HubItemLayoutFormModel;
	}
>;

export type EditAnnouncementDialogMode = {
	kind: "edit";
	announcementId: number;
} | {
	kind: "new";
} | {
	kind: "new-from-template";
	templateId: number;
} | {
	kind: "duplicate";
	announcementToCopyId: number;
};

export type EditDialogProps = {
	mode: EditAnnouncementDialogMode;
	onClose(wasAnyCategoryCreated: boolean): void;
	onSave?(): void;
	onBack?(wasAnyCategoryCreated: boolean): void;
	preferredCategoryId?: number | null;
};

export const EditDialog = (props: EditDialogProps): ReactElement => {
	const {
		mode: modeFromProps,
		onClose: onCloseFromProps,
		onSave,
		onBack,
		preferredCategoryId = null
	} = props;

	const mode = useValueEqualMemo(
		(): EditAnnouncementDialogMode => modeFromProps,
		[modeFromProps]
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [activeStep, setActiveStep] = useState<FormStep>(FormStep.Content);
	const [isInPreviewMode, setIsInPreviewMode] = useState<boolean>(false);
	const [finishedSteps, setFinishedSteps] = useState<ReadonlySet<FormStep>>(new Set());
	const [hasServerError, setHasServerError] = useState<boolean>(false);
	const [wasAnyCategoryCreated, setWasAnyCategoryCreated] = useState<boolean>(false);
	const [backdropState, setBackdropState] = useState<DialogBackdropState>();
	const [shouldFocusTitle, setShouldFocusTitle] = useState<boolean>(
		mode.kind === "new"
			? true
			: !isMobile
	);

	const [formData, setFormData] = useState<RequestFormModel>({
		announcementSaveModel: {
			title: "",
			startTime: null,
			endTime: null,
			areCommentsEnabled: false,
			categoryIds: [],
			isForEveryoneInTheCategories: true,
			media: {
				mediaKind: HubItemMediaKind.None
			},
			showAutomatically: false,
			recipients: null,
			shouldShowAuthor: false,
			layout: { rows: [] },
			isPinned: false,
			tags: [],
			linkedDocuments: []
		},
		shouldPublishAnnouncement: false
	});
	const [initialFormData, setInitialFormData] = useState<RequestFormModel>();
	const [state, setState] = useState<EditAnnouncementState>({ isLoaded: false });
	const [hasStartTime, setHasStartTime] = useState<boolean>(false);
	const [hasEndTime, setHasEndTime] = useState<boolean>(false);

	const {
		canCreateNewCategory = false,
		canSendDirectPopups = false,
		categoryHierarchy = EMPTY_ARRAY,
		imageRestrictions = {
			maxImageSize: 0,
			supportedFileExtensions: EMPTY_ARRAY,
			supportedMimeTypes: EMPTY_ARRAY
		},
		defaultImageLocation = null,
		originalImages = EMPTY_IMAGE_SET,
		status = AnnouncementStatus.Draft,
		usersAndUserGroupsData = EMPTY_USER_AND_USERGROUP_DATA,
		isPixabayImageGalleryAvailable = false
	} = state;

	const contentStepRef: RefObject<ContentStepHandle | null> = useRef<ContentStepHandle>(null);
	const alertManager: AlertManager = useAlerts();
	const onClose: typeof onCloseFromProps = useStableImperativeCallback(onCloseFromProps);
	const userTimezone: TimeZone = useUserTimezone();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const infoAlerts: ReactElement = useInfoAlerts(
		useMemo(
			(): readonly InfoAlertOptions[] => [
				{
					uniqueKey: "archived-message",
					message: localize("_ThisAnnouncementCantBeEditedBecauseArchived"),
					shouldShow: state.status === AnnouncementStatus.Archived,
				},
				{
					uniqueKey: "duplicate-info",
					message: localize("_DuplicateHubItemInfoMessage"),
					shouldShow: mode.kind === "duplicate"
				}
			],
			[mode.kind, state.status]
		)
	);

	const [leaveComposerDialogElement, openLeaveComposerDialog] = useHubComposerUnsavedChangesDialog();
	const [editCategoryDialog, openEditCategoryDialog] = useEditHubCategoryDialog();
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<RequestFormModel> => {
			const result: IValidationConfiguration<RequestFormModel> = staticValidationConfiguration.clone();

			const saveModelValidation: IObjectValidatorBuilder<AnnouncementFormModel> = result.forObject("announcementSaveModel");

			saveModelValidation.forObject("media")
				.addValidators(
					createMediaValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			saveModelValidation.forObject("layout")
				.addValidators(
					createLayoutValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			if (hasStartTime) {
				saveModelValidation.forScalar("startTime")
					.add(isNotNullNorUndefined());
			}

			if (hasEndTime) {
				saveModelValidation.forScalar("endTime")
					.add(isNotNullNorUndefined());
			}

			return result;
		},
		[
			hasEndTime,
			hasStartTime,
			imageRestrictions.supportedMimeTypes,
			imageRestrictions.maxImageSize
		]
	);

	const formHandler: IFormHandler<RequestFormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const attachable: IValueInputAttachable<AnnouncementFormModel> = formHandler.for("announcementSaveModel");

	const [pinnedAnnouncementDialog, openPinnedAnnouncementDialog] = usePinnedAnnouncementsDialog();

	const steps = useMemo(
		(): Step<FormStep>[] => {
			return initialSteps.map((x): Step<FormStep> => ({
				...x,
				isFinished: finishedSteps.has(x.value) || mode.kind === "edit",
				hasError: formHandler
					.forStep(formStepToValidationStep[x.value])
					.validationState
					.kind === InputValidationResultKind.Invalid
			}));
		},
		[
			mode.kind,
			finishedSteps,
			formHandler
		]
	);

	const newCategoryHandler = useCallback(
		async (): Promise<void> => {
			setBackdropState("invisible");

			const newCategoryResult: TryResult<HubCategorySavedResult> = await openEditCategoryDialog({
				kind: HubItemKind.Announcement,
				id: null
			});

			setBackdropState("visible");
			if (!newCategoryResult.wasSuccessful) {
				return;
			}

			const {
				id,
				name
			} = newCategoryResult.result;

			setState(
				state => {
					if (!state.isLoaded) {
						console.error("Cannot create new category in unloaded state.");
						return state;
					}

					const categoryNode: HubHierarchyListCategoryNodeModel = {
						kind: HubItemCategoryNodeKind.Category,
						category: {
							id,
							name,
							canCreateItem: true,
							isFollowing: false,
							kind: HubItemCategoryKind.Custom,
							hubItemKind: HubItemKind.Announcement
						}
					};

					return {
						...state,
						categoryHierarchy: [
							...state.categoryHierarchy,
							categoryNode
						]
					};
				}
			);

			setWasAnyCategoryCreated(true);
			setFormData(
				(formData): RequestFormModel => ({
					...formData,
					announcementSaveModel: {
						...formData.announcementSaveModel,
						categoryIds: [
							...formData.announcementSaveModel
								.categoryIds,
							id
						]
					}
				})
			);

			formHandler
				.validation
				.for("announcementSaveModel")
				.for("categoryIds")
				.triggerInputFinished();
		},
		[
			formHandler.validation,
			openEditCategoryDialog
		]
	);

	useEffect(
		() => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					let model: EditAnnouncementPageViewModel;

					switch (mode.kind) {
						case "new":
							model = await getNewModel(
								undefined,
								undefined,
								signal
							);
							break;
						case "new-from-template":
							model = await getNewModel(
								undefined,
								mode.templateId,
								signal
							);
							break;
						case "duplicate":
							model = await getNewModel(
								mode.announcementToCopyId,
								undefined,
								signal
							);
							break;
						case "edit":
							model = await getEditModel(mode.announcementId, signal);
							break;
						default:
							never(mode);
					}

					const {
						announcement,
						announcementHierarchy,
						usersAndUserGroupsData,
						canCreateNewCategory,
						canSendDirectPopups,
						status,
						imageRestrictions,
						defaultImageLocation,
						createdBy,
						isPixabayImageGalleryAvailable
					} = model;

					let existingPreferredCategoryId: number | null = null;

					if (isNotNil(preferredCategoryId)) {
						const tryFindCategoryResult: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(announcementHierarchy, preferredCategoryId);

						if (tryFindCategoryResult.wasSuccessful) {
							existingPreferredCategoryId = tryFindCategoryResult
								.result
								.category
								.id;
						}
					}

					const saveModel: RequestFormModel = await convertToAnnouncementFormModel(
						announcement,
						existingPreferredCategoryId
					);

					setState({
						isLoaded: true,
						categoryHierarchy: announcementHierarchy,
						usersAndUserGroupsData,
						canCreateNewCategory,
						canSendDirectPopups,
						imageRestrictions,
						defaultImageLocation,
						status,
						originalImages: getOriginalImages(announcement),
						originalViewModel: announcement,
						createdBy,
						isPixabayImageGalleryAvailable
					});

					setHasStartTime(isNotNil(announcement.startTime));
					setHasEndTime(isNotNil(announcement.endTime));

					setInitialFormData(saveModel);
					setFormData(saveModel);

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			mode,
			onClose,
			preferredCategoryId
		]
	);

	const checkPinItemPossibility = useCallback(
		async (): Promise<boolean> => {
			const {
				categoryIds,
				isPinned
			} = formData.announcementSaveModel;

			if (categoryIds.length === 0) {
				return false;
			}

			if (!isPinned) {
				return true;
			}

			const id: number | null = mode.kind === "edit"
				? mode.announcementId
				: null;

			const configuredCategories: readonly HubHierarchyListCategoryNodeModel[] = findHubCategoryItemNodes(
				categoryHierarchy,
				categoryIds
			);

			for (const configuredCategory of configuredCategories) {
				const { category } = configuredCategory;

				try {
					const results: CheckAnnouncementPinLimitResult = await checkPinLimit(category.id, id);

					if (results.canBePinned) {
						continue;
					}

					const result: TryResult<void> = await openPinnedAnnouncementDialog({
						pinnedItems: results.pinnedItems,
						categoryName: category.name
					});

					if (result.wasSuccessful) {
						continue;
					}

					setActiveStep(FormStep.Settings);
					attachable.for("isPinned")
						.reducer
						.setValue(false);
					return false;
				} catch(e) {
					if (isCancelledError(e)) {
						return false;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));

					return false;
				}
			}

			return true;
		},
		[
			alertManager,
			attachable,
			categoryHierarchy,
			formData.announcementSaveModel,
			openPinnedAnnouncementDialog,
			mode
		]
	);

	const tryEnsureValidShowAutomaticallySetting = useCallback(
		async (formData: RequestFormModel, isDraft: boolean = false): Promise<TryResult<RequestFormModel>> => {
			if (!canSendDirectPopups
				&& formData
					.announcementSaveModel
					.showAutomatically
			) {
				const result: TryResult<void> = await openConfirmationDialog(
					getDisableTheShowAutomaticallySettingDialogProps(isDraft)
				);

				if (!result.wasSuccessful) {
					return { wasSuccessful: false };
				}

				const showAutomaticallyHandler: IValueInputAttachable<boolean> = formHandler
					.for("announcementSaveModel")
					.for("showAutomatically");

				showAutomaticallyHandler
					.reducer
					.setValue(false);

				showAutomaticallyHandler
					.validation
					.triggerInputFinished();

				return {
					wasSuccessful: true,
					result: {
						...formData,
						announcementSaveModel: {
							...formData.announcementSaveModel,
							showAutomatically: false
						}
					}
				};
			}

			return {
				wasSuccessful: true,
				result: formData
			};
		},
		[
			canSendDirectPopups,
			formHandler,
			openConfirmationDialog
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (status === AnnouncementStatus.Archived) {
				return;
			}

			setIsBusy(true);
			let serverValidationPathRedirects: readonly PathRedirect[] | undefined = undefined;

			try {
				const result: InputValidationResult = await formHandler
					.validation
					.validate();

				if (result.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					setIsInPreviewMode(false);
					return;
				}

				const isPinCheckSuccessful = await checkPinItemPossibility();

				if (!isPinCheckSuccessful) {
					setIsBusy(false);
					return;
				}

				let model: RequestFormModel = formData;

				if (mode.kind === "new"
					|| status !== AnnouncementStatus.Published
				) {
					const ensureValidShowAutomaticallySettingResult: TryResult<RequestFormModel> = await tryEnsureValidShowAutomaticallySetting(model);

					if (!ensureValidShowAutomaticallySettingResult.wasSuccessful) {
						setIsBusy(false);
						return;
					}

					model = ensureValidShowAutomaticallySettingResult.result;
				}

				switch (mode.kind) {
					case "new":
					case "duplicate":
					case "new-from-template":
						await create(
							{
								announcementSaveModel: preparePlannedAnnouncement(
									model.announcementSaveModel,
									hasStartTime,
									hasEndTime,
									false
								),
								shouldPublishAnnouncement: true
							},
							globalCancellation
						);
						break;
					case "edit":
						if (status === AnnouncementStatus.Published) {
							serverValidationPathRedirects = [
								{
									pattern: new DotPathWildcardPattern("**"),
									replacement: "AnnouncementSaveModel.{0}"
								}
							];

							await updatePublished(
								mode.announcementId,
								preparePublishedAnnouncement(model.announcementSaveModel, hasEndTime),
								globalCancellation
							);
						} else {
							await update(
								mode.announcementId,
								{
									announcementSaveModel: preparePlannedAnnouncement(
										model.announcementSaveModel,
										hasStartTime,
										hasEndTime,
										false
									),
									shouldPublishAnnouncement: true
								},
								globalCancellation
							);
						}
						break;
					default:
						never(mode);
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullySubmittedTheAnnouncement"));
				onSave?.();
				onClose(wasAnyCategoryCreated);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				const wasHandled: boolean = formHandler
					.validation
					.tryProcessServerError(e, serverValidationPathRedirects);

				if (wasHandled) {
					setIsInPreviewMode(false);
				} else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setHasServerError(true);
			}
		},
		[
			alertManager,
			mode,
			checkPinItemPossibility,
			tryEnsureValidShowAutomaticallySetting,
			formData,
			formHandler.validation,
			globalCancellation,
			hasEndTime,
			hasStartTime,
			onClose,
			onSave,
			status,
			wasAnyCategoryCreated
		]
	);

	const saveDraftHandler = useCallback(
		async (): Promise<void> => {
			if (status !== AnnouncementStatus.Draft) {
				return;
			}

			setIsBusy(true);

			try {
				const result: InputValidationResult = await formHandler
					.validation
					.validate();

				if (result.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					setIsInPreviewMode(false);
					return;
				}

				const isPinCheckSuccessful = await checkPinItemPossibility();

				if (!isPinCheckSuccessful) {
					setIsBusy(false);
					return;
				}

				let model: RequestFormModel = formData;

				const ensureValidShowAutomaticallySettingResult: TryResult<RequestFormModel> = await tryEnsureValidShowAutomaticallySetting(model, true);

				if (!ensureValidShowAutomaticallySettingResult.wasSuccessful) {
					setIsBusy(false);
					return;
				}

				model = ensureValidShowAutomaticallySettingResult.result;

				switch (mode.kind) {
					case "new":
					case "new-from-template":
					case "duplicate":
						await create(
							{
								announcementSaveModel: preparePlannedAnnouncement(
									model.announcementSaveModel,
									hasStartTime,
									hasEndTime,
									true
								),
								shouldPublishAnnouncement: false
							},
							globalCancellation
						);
						break;
					case "edit":
						await update(
							mode.announcementId,
							{
								announcementSaveModel: preparePlannedAnnouncement(
									model.announcementSaveModel,
									hasStartTime,
									hasEndTime,
									true
								),
								shouldPublishAnnouncement: false
							},
							globalCancellation
						);
						break;
					default:
						never(mode);
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullySavedAsDraft"));
				onSave?.();
				onClose(wasAnyCategoryCreated);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				const wasHandled: boolean = formHandler
					.validation
					.tryProcessServerError(e);

				if (wasHandled) {
					setIsInPreviewMode(false);
				} else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setHasServerError(true);
			}
		},
		[
			alertManager,
			mode,
			checkPinItemPossibility,
			tryEnsureValidShowAutomaticallySetting,
			formData,
			formHandler.validation,
			globalCancellation,
			hasEndTime,
			hasStartTime,
			onClose,
			onSave,
			status,
			wasAnyCategoryCreated
		]
	);

	useEffect(
		() => {
			if (
				formHandler.validation
					.getValidationState()
					.kind === InputValidationResultKind.Invalid
						&& hasServerError
			) {
				for (const step of steps) {
					const hasError = formHandler.validation
						.getValidationStepStates(formStepToValidationStep[step.value])
						.some(x => x.kind === InputValidationResultKind.Invalid);

					if (hasError) {
						setActiveStep(step.value);
						setHasServerError(false);
						break;
					}
				}
			}
		},
		[
			formHandler.validation,
			hasServerError,
			steps
		]
	);

	useEffect(
		(): void => {
			if (activeStep !== FormStep.Content) {
				setShouldFocusTitle(false);
			}
		},
		[activeStep]
	);

	const previousStep = useMemo(
		(): FormStep | undefined => {
			const activeIndex: number = steps.findIndex(s => s.value === activeStep);

			return steps[activeIndex - 1]?.value;
		},
		[
			activeStep,
			steps
		]
	);

	const nextStep = useMemo(
		(): FormStep | undefined => {
			const activeIndex: number = steps.findIndex(s => s.value === activeStep);

			return steps[activeIndex + 1]?.value;
		},
		[
			activeStep,
			steps
		]
	);

	const goToPreviousStep = useCallback(
		(): void => {
			if (isNil(previousStep)) {
				return;
			}
			setActiveStep(previousStep);
		},
		[
			previousStep
		]
	);

	const goToNextStep = useCallback(
		async (): Promise<void> => {
			if (isNil(nextStep)) {
				return;
			}

			const stepValidationResult: InputValidationResult = await formHandler
				.validation
				.validateStep(formStepToValidationStep[activeStep]);

			if (stepValidationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			if(activeStep === FormStep.Settings) {
				const result: boolean = await checkPinItemPossibility();

				if(!result) {
					return;
				}
			}

			setFinishedSteps(x => new Set([...x, activeStep, nextStep]));
			setActiveStep(nextStep);
		},
		[
			activeStep,
			checkPinItemPossibility,
			formHandler.validation,
			nextStep
		]
	);

	const isPublishAvailable = useMemo(
		(): boolean => {
			if (status === AnnouncementStatus.Archived) {
				return false;
			}

			const isFormValid: boolean = formHandler.validation
				.getValidationState()
				.kind === InputValidationResultKind.Valid;

			return steps
				.every(
					x => x.isFinished === true || x.value === activeStep
				)
					&& isFormValid;
		},
		[
			formHandler.validation,
			steps,
			activeStep,
			status
		]
	);

	const isSaveAsDraftAvailable = useMemo(
		(): boolean => {
			if (status !== AnnouncementStatus.Draft) {
				return false;
			}

			return Boolean(
				steps.find(
					x => x.value === FormStep.Settings
				)?.isFinished
			);
		},
		[status, steps]
	);

	const isDataChanged = useMemo(
		(): boolean => isNotNil(initialFormData)
			&& !deepComparison(formData, initialFormData),
		[
			formData,
			initialFormData
		]
	);

	const activeStepChangeHandler = useCallback(
		(value: FormStep): void => {
			setFinishedSteps(x => new Set([...x, activeStep]));
			setActiveStep(value);
		},
		[activeStep]
	);

	const onBeforeUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			const isChanged: boolean = isDataChanged
				|| (
					contentStepRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				beforeUnloadListener(event);
			}
		},
		[isDataChanged]
	);

	useEffect(
		() => {
			window
				.addEventListener(
					"beforeunload",
					onBeforeUnload
				);

			return () => window
				.removeEventListener(
					"beforeunload",
					onBeforeUnload
				);
		},
		[onBeforeUnload]
	);

	const leaveWithConfirmationCheck = useCallback(
		async (leaveAction: () => void): Promise<void> => {
			const isChanged: boolean = isDataChanged
				|| (
					contentStepRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				const confirmation: TryResult<boolean> = await openLeaveComposerDialog({
					shouldShowSaveAsDraft: isSaveAsDraftAvailable
				});

				if (confirmation.wasSuccessful) {
					if (confirmation.result) {
						await saveDraftHandler();
					} else {
						leaveAction();
					}
				}
			} else {
				leaveAction();
			}
		},
		[
			isDataChanged,
			isSaveAsDraftAvailable,
			openLeaveComposerDialog,
			saveDraftHandler
		]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			return onBack
				? () => leaveWithConfirmationCheck(
					() => onBack(wasAnyCategoryCreated)
				)
				: undefined;
		},
		[
			leaveWithConfirmationCheck,
			onBack,
			wasAnyCategoryCreated
		]
	);

	const closeHandler = useCallback(
		(): void => {
			leaveWithConfirmationCheck(
				() => onClose(wasAnyCategoryCreated)
			);
		},
		[
			onClose,
			wasAnyCategoryCreated,
			leaveWithConfirmationCheck
		]
	);


	const renderFooter = (): ReactElement =>(
		<Toolbar>
			{!isMobile && (
				<>
					<Toolbar.Toggle
						value={isInPreviewMode}
						onChange={setIsInPreviewMode}
						text={localize("_Preview")}
						trackingId="preview-mode"
						shouldAutoCloseResponsiveDropOver
					/>
					<Toolbar.SpaceBetween />
				</>
			)}
			<Toolbar.Button
				kind="secondary"
				text={localize("_Previous")}
				isDisabled={isInPreviewMode || isNil(previousStep)}
				onClick={goToPreviousStep}
				trackingId="previous"
			/>
			{state.status === AnnouncementStatus.Draft && (
				<Toolbar.Button
					trackingId="save-draft"
					kind="secondary"
					text={localize("_SaveAsDraft")}
					onClick={saveDraftHandler}
					isDisabled={!isSaveAsDraftAvailable}
				/>
			)}
			<Toolbar.Button
				kind="primary"
				text={localize("_Next")}
				isDisabled={isInPreviewMode || isNil(nextStep)}
				onClick={goToNextStep}
				trackingId="next"
			/>
			<Toolbar.Button
				kind="primary"
				text={
					localize(
						hasStartTime
							&& isNotNil(
								formData.announcementSaveModel
									.startTime
							)
								? "_Submit"
								: "_Publish"
					)
				}
				isDisabled={!isPublishAvailable}
				onClick={saveHandler}
				trackingId="publish"
			/>
		</Toolbar>
	);

	const renderPreview = (): ReactElement | null => {
		if (!isInPreviewMode
			|| !state.isLoaded
			|| isMobile
		) {
			return null;
		}

		return (
			<Preview
				model={formData.announcementSaveModel}
				validation={formHandler.validation}
				createdBy={state.createdBy}
				timezone={userTimezone}
				categoryHierarchy={state.categoryHierarchy}
				hasStartTime={hasStartTime}
				originalImages={state.originalImages}
			/>
		);
	};

	return (
		<>
			<HubItemComposerDialog
				title={
					mode.kind === "new" || mode.kind === "duplicate"
						? localize("_NewAnnouncement")
						: localize("_EditAnnouncement")
				}
				onClose={closeHandler}
				onBack={backHandler}
				activeStep={activeStep}
				goToNextStep={goToNextStep}
				isBusy={isBusy}
				onActiveStepChanged={activeStepChangeHandler}
				steps={steps}
				footer={renderFooter()}
				preview={renderPreview()}
				trackingId="announcement-composer-dialog"
				backdrop={backdropState}
			>
				{status === AnnouncementStatus.Archived && state.isLoaded
					? (
						<ArchivedContentStep
							isActive={activeStep === FormStep.Content}
							model={formData.announcementSaveModel}
							onChangeIsLoading={setIsBusy}
							originalImages={originalImages}
							infoAlerts={infoAlerts}
						/>
					)
					: (
						<ContentStep
							ref={contentStepRef}
							isActive={activeStep === FormStep.Content}
							attachable={attachable}
							imageRestrictions={imageRestrictions}
							defaultImageLocation={defaultImageLocation}
							originalImages={originalImages}
							onChangeIsLoading={setIsBusy}
							shouldFocusTitle={shouldFocusTitle}
							infoAlerts={infoAlerts}
							isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
						/>
					)
				}
				<SettingsStep
					isActive={activeStep === FormStep.Settings}
					attachable={attachable}
					status={status}
					categoryHierarchy={categoryHierarchy}
					isEditingExistingAnnouncement={mode.kind === "edit"}
					hasPermissionToCreateNewCategory={canCreateNewCategory}
					hasPermissionToSendDirectPopups={canSendDirectPopups}
					onCreateNewCategory={newCategoryHandler}
					timezone={userTimezone}
					hasStartTime={hasStartTime}
					onHasStartTimeChange={setHasStartTime}
					hasEndTime={hasEndTime}
					onHasEndTimeChange={setHasEndTime}
					infoAlerts={infoAlerts}
				/>
				<RecipientsStep
					isActive={activeStep === FormStep.Recipients}
					attachable={attachable}
					status={status}
					usersAndUserGroupsData={usersAndUserGroupsData}
					infoAlerts={infoAlerts}
				/>
			</HubItemComposerDialog>
			{editCategoryDialog}
			{pinnedAnnouncementDialog}
			{confirmationDialogElement}
			{leaveComposerDialogElement}
		</>
	);
};
EditDialog.displayName="EditDialog";

type RequestFormModel = OverrideProperties<
	AnnouncementSaveRequestModel,
	{
		announcementSaveModel: AnnouncementFormModel;
	}
>;

const staticValidationConfiguration: IValidationConfiguration<RequestFormModel> = createValidationConfiguration<RequestFormModel>();
const announcementValidationConfiguration: IObjectValidatorBuilder<AnnouncementFormModel> = staticValidationConfiguration.forObject("announcementSaveModel");

announcementValidationConfiguration.forScalar("title")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

announcementValidationConfiguration.forScalar("categoryIds")
	.add(
		isNotEmpty(),
		hasMaximumEntries(10),
	);

announcementValidationConfiguration
	.forCollection("linkedDocuments")
	.add(
		hasMaximumEntries(MAX_LINKED_DOCUMENTS_COUNT)
	);

announcementValidationConfiguration
	.forCollection("linkedDocuments")
	.forItems(getLinkedDocumentId)
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

announcementValidationConfiguration
	.forCollection("tags")
	.add(
		hasMaximumEntries(MAX_TAG_COUNT)
	);

announcementValidationConfiguration.forScalar("startTime")
	.add(
		isValidDate()
	);

announcementValidationConfiguration.forScalar("endTime")
	.add(
		isValidDate()
	);

const contentValidationStep: IObjectValidationStepBuilder<AnnouncementSaveModel> = configureValidationStep<RequestFormModel>()
	.for("announcementSaveModel")
	.add(
		"title",
		"layout",
		"media",
		"linkedDocuments"
	);

const settingsValidationStep: IObjectValidationStepBuilder<AnnouncementSaveModel> = configureValidationStep<RequestFormModel>()
	.for("announcementSaveModel")
	.add(
		"categoryIds",
		"showAutomatically",
		"shouldShowAuthor",
		"areCommentsEnabled",
		"startTime",
		"endTime",
		"isPinned",
		"tags"
	);

const recipientsValidationStep: IObjectValidationStepBuilder<AnnouncementSaveModel> = configureValidationStep<RequestFormModel>()
	.for("announcementSaveModel")
	.add(
		"isForEveryoneInTheCategories",
		"recipients"
	);

const formStepToValidationStep: { [key in FormStep]: IObjectValidationStepBuilder<AnnouncementSaveModel> } = {
	[FormStep.Content]: contentValidationStep,
	[FormStep.Settings]: settingsValidationStep,
	[FormStep.Recipients]: recipientsValidationStep,
};

const initialSteps: readonly Step<FormStep>[] = [
	{
		title: localize("_Content"),
		value: FormStep.Content,
		valueTrackingId: "content-step"
	},
	{
		title: localize("_Settings"),
		value: FormStep.Settings,
		valueTrackingId: "settings-step"
	},
	{
		title: localize("_Recipients"),
		value: FormStep.Recipients,
		valueTrackingId: "recipients-step"
	}
];

export type EditAnnouncementDialogInitialData = Omit<EditDialogProps, "onClose" | "onSave">;

export function useEditAnnouncementDialog(): [
	element: ReactElement | null,
	open: (props: EditAnnouncementDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditDialog
				{...props}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

async function convertToAnnouncementFormModel(
	viewModel: AnnouncementViewModel,
	preferredCategoryId: number | null
): Promise<RequestFormModel> {
	return {
		announcementSaveModel: {
			title: viewModel.title ?? "",
			layout: isNotNil(viewModel.layout)
				? await convertLayoutToSaveModel(viewModel.layout)
				: { rows: [] },
			areCommentsEnabled: viewModel.areCommentsEnabled,
			media: await convertMediaToSaveModel(viewModel.media),
			categoryIds: viewModel.categoryIds
				.length > 0
					? viewModel.categoryIds
					: isNotNil(preferredCategoryId)
						? [preferredCategoryId]
						: [],
			startTime: parseDtoDate(viewModel.startTime),
			endTime: parseDtoDate(viewModel.endTime),
			isForEveryoneInTheCategories: viewModel.isForEveryoneInTheCategories,
			showAutomatically: viewModel.showAutomatically,
			recipients: viewModel.recipients,
			shouldShowAuthor: viewModel.shouldShowAuthor,
			isPinned: viewModel.isPinned,
			tags: [...viewModel.tags],
			linkedDocuments: viewModel
				.linkedDocuments
				?.map(convertToLinkedDocumentFormData) ?? []
		},
		shouldPublishAnnouncement: false
	};
}

function getOriginalImages(viewModel: AnnouncementViewModel): ExistingHubItemImages {
	const coverImage: BlobIdImageHubMediaComposerViewModel | null = viewModel.media.mediaKind === HubItemMediaKind.Image
		&& viewModel.media.imageKind === HubImageMediaKind.BlobId
			? viewModel.media
			: null;

	const layoutImages: Map<number, BlobIdImageElementComposerViewModel> = isNotNil(viewModel.layout)
		? getOriginalImagesFromLayout(viewModel.layout)
		: new Map();

	return {
		coverImage,
		layoutImages
	};
}

function preparePlannedAnnouncement(
	announcement: AnnouncementFormModel,
	hasStartTime: boolean,
	hasEndTime: boolean,
	isDraft: boolean
): AnnouncementSaveModel {
	return {
		...announcement,
		media: cleanUpMedia(announcement.media),
		linkedDocuments: announcement.linkedDocuments
			.map(convertToLinkedDocumentSaveModel),
		startTime: hasStartTime
			? ensureNotNil(announcement.startTime)
			: isDraft
				? null
				: new Date(),
		endTime: hasEndTime
			? ensureNotNil(announcement.endTime)
			: null
	};
}

function preparePublishedAnnouncement(
	announcement: AnnouncementFormModel,
	hasEndTime: boolean
): PublishedAnnouncementSaveModel {
	const {
		title,
		categoryIds,
		shouldShowAuthor,
		areCommentsEnabled,
		endTime,
		media,
		layout,
		isPinned,
		tags,
		linkedDocuments
	} = announcement;

	return {
		title,
		layout,
		categoryIds,
		linkedDocuments: linkedDocuments.map(convertToLinkedDocumentSaveModel),
		shouldShowAuthor,
		areCommentsEnabled,
		endTime: hasEndTime
			? ensureNotNil(endTime)
			: null,
		isPinned,
		tags,
		media: cleanUpMedia(media)
	};
}

type EditAnnouncementState = ConditionalProps<
	{
		originalViewModel: AnnouncementViewModel;
		originalImages: ExistingHubItemImages;
		categoryHierarchy: readonly HubHierarchyListNodeModel[];
		usersAndUserGroupsData: UsersAndUserGroupsDataModel;
		canCreateNewCategory: boolean;
		canSendDirectPopups: boolean;
		status: AnnouncementStatus;
		imageRestrictions: HubItemImageRestrictionsModel;
		defaultImageLocation: DocumentLocationViewModel | null;
		createdBy: UserIdentityModel | null;
		isPixabayImageGalleryAvailable: boolean;
	},
	"isLoaded"
>;

const EMPTY_IMAGE_SET: ExistingHubItemImages = {
	coverImage: null,
	layoutImages: new Map()
};

const EMPTY_USER_AND_USERGROUP_DATA: UsersAndUserGroupsDataModel = {
	users: [],
	userGroups: []
};

function beforeUnloadListener(event: BeforeUnloadEvent): string {
	event.preventDefault();
	event.returnValue = localize("_YouHaveUnsavedChanges");
	return event.returnValue;
};